import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Image, Modal } from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

// Fallback type definition if TypeScript still complains
interface EmailJSResponseStatus {
  status: number;
  text: string;
}

const BecomeMember: React.FC = () => {
  const [formData, setFormData] = useState({
    memberId: '',
    fullName: '',
    mobile: '',
    email: '',
    aadhar: '',
    referenceBy: '',
    gender: '',
    dateOfBirth: '',
    fathersName: '',
    state: '',
    city: '',
    pinCode: '',
    address: '',
    nationality: 'India',
    qualification: '',
    areaOfOperation: '',
    experience: '',
    briefAboutYourself: '',
    termsAccepted: false,
    photo: null as File | null,
    resume: null as File | null,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // Modal visibility state

  useEffect(() => {
    const generateMemberId = () => {
      const randomId = 'MEM' + Math.floor(1000 + Math.random() * 9000);
      setFormData((prev) => ({ ...prev, memberId: randomId }));
    };
    generateMemberId();
  }, []);

  // Show modal and auto-close after 3 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      setShowModal(true);
      const timer = setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000); // Modal closes after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'photo' | 'resume') => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setFormData((prev) => ({ ...prev, [field]: file }));
      if (field === 'photo' && file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewPhoto(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, termsAccepted: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      setErrorMessage('Please accept the Terms and Conditions.');
      return;
    }

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value instanceof File) {
        formDataToSend.append(key, value);
      } else if (value !== null) {
        formDataToSend.append(key, value.toString());
      }
    });

    try {
      const res = await fetch(process.env.REACT_APP_API_URL + '/api/members', {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await res.json();
      if (res.ok) {
        setSuccessMessage('Thank you for registering as a member!');

        // Send confirmation email to the user
        emailjs.send(
          'service_3tx8jli', // Your EmailJS service ID
          'template_q6h2b6a', // Your EmailJS template ID
          {
            member_id: formData.memberId,
            name: formData.fullName,
            email: formData.email,
            to_email: formData.email, // Ensure this is the user's email
          },
          '2znub3nsdivNcJHnz' // Your EmailJS user ID
        ).then(
          (result: EmailJSResponseStatus) => {
            console.log('Confirmation email sent successfully to:', formData.email, result.text);
          },
          (error: EmailJSResponseStatus) => {
            console.error('Failed to send confirmation email:', error.text);
            setErrorMessage('Form submitted, but failed to send confirmation email.');
          }
        );

        const randomId = 'MEM' + Math.floor(1000 + Math.random() * 9000);
        setFormData({
          memberId: randomId,
          fullName: '',
          mobile: '',
          email: '',
          aadhar: '',
          referenceBy: '',
          gender: '',
          dateOfBirth: '',
          fathersName: '',
          state: '',
          city: '',
          pinCode: '',
          address: '',
          nationality: 'India',
          qualification: '',
          areaOfOperation: '',
          experience: '',
          briefAboutYourself: '',
          termsAccepted: false,
          photo: null,
          resume: null,
        });
        setPreviewPhoto(null);
      } else {
        setErrorMessage(data.message || 'Error submitting form.');
      }
    } catch (err) {
      setErrorMessage('Error submitting form. Please try again.');
    }
  };

  return (
    <section className="become-member-section mb-5">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Become a Member</h1>
          <h3 className="hero-subtitle">Join Our Noble Cause</h3>
          <p className="hero-text">
            Be a part of our mission to transform lives through education, healthcare, and empowerment. Together, we can create lasting change—your support truly matters!.
          </p>
        </div>
      </div>

      <Container>
        <Row className="align-items-start mt-5">
          {/* Left Section (Col-6) */}
          <Col md={6} className="mb-4">
            <div className="member-form-box shadow-lg">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formMemberId">
                  <Form.Label className="d-none">Member ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="memberId"
                    value={formData.memberId}
                    readOnly
                    className="form-control-custom d-none"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formFullName">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formMobile">
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    pattern="[0-9]{10}"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formAadhar">
                  <Form.Label>Aadhar (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="aadhar"
                    value={formData.aadhar}
                    onChange={handleChange}
                    placeholder="Enter your Aadhar number"
                    pattern="[0-9]{12}"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formReferenceBy">
                  <Form.Label>Reference By (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="referenceBy"
                    value={formData.referenceBy}
                    onChange={handleChange}
                    placeholder="Who referred you?"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formGender">
                  <Form.Label>Gender</Form.Label>
                  <Form.Control
                    as="select"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formDateOfBirth">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formFathersName">
                  <Form.Label>Father’s Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fathersName"
                    value={formData.fathersName}
                    onChange={handleChange}
                    placeholder="Enter your father’s name"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formState">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="Enter your state"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formCity">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter your city"
                    required
                  />
                </Form.Group>
              </Form>
            </div>
          </Col>

          {/* Right Section (Col-6) */}
          <Col md={6} className="mb-4">
            <div className="member-form-box shadow-lg">
              {/* Photo Preview at the Top Right */}
              {previewPhoto && (
                <div className="photo-preview mb-3">
                  <Image src={previewPhoto} alt="Preview" rounded fluid />
                </div>
              )}
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formPinCode">
                  <Form.Label>Pin Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="pinCode"
                    value={formData.pinCode}
                    onChange={handleChange}
                    placeholder="Enter your pin code"
                    pattern="[0-9]{6}"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formAddress">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter your address"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formNationality">
                  <Form.Label>Nationality</Form.Label>
                  <Form.Control
                    as="select"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    required
                  >
                    <option value="India">India</option>
                    <option value="Other">Other</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formQualification">
                  <Form.Label>Qualification</Form.Label>
                  <Form.Control
                    type="text"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    placeholder="Enter your qualification"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formAreaOfOperation">
                  <Form.Label>Area of Operation</Form.Label>
                  <Form.Control
                    type="text"
                    name="areaOfOperation"
                    value={formData.areaOfOperation}
                    onChange={handleChange}
                    placeholder="Enter your area of operation"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formExperience">
                  <Form.Label>Experience (if any)</Form.Label>
                  <Form.Control
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Enter your experience"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formBriefAboutYourself">
                  <Form.Label>Brief About Yourself</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="briefAboutYourself"
                    value={formData.briefAboutYourself}
                    onChange={handleChange}
                    placeholder="Tell us about yourself"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPhoto">
                  <Form.Label>Photo (Optional)</Form.Label>
                  <Form.Control
                    type="file"
                    name="photo"
                    accept="image/*"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, 'photo')}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formResume">
                  <Form.Label>Resume (Optional)</Form.Label>
                  <Form.Control
                    type="file"
                    name="resume"
                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, 'resume')}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formTerms">
                  <Form.Check
                    type="checkbox"
                    label="I accept the Terms and Conditions"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleCheckboxChange}
                    required
                  />
                </Form.Group>
                <div className="text-center">
                  <Button variant="primary" type="submit" className="member-submit-btn">
                    Submit Application
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>

        {/* Success/Error Modal */}
        <Modal show={showModal} centered>
          <Modal.Body className="text-center">
            {successMessage ? (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                <h4 className="text-success mb-2">Success!</h4>
                <p>{successMessage}</p>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
                <h4 className="text-danger mb-2">Error!</h4>
                <p>{errorMessage}</p>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </section>
  );
};

export default BecomeMember;